import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { http } from '@/shared/lib/api';

export type PickupStatus = 'planned' | 'rescheduled' | 'done' | 'canceled';
export type PickupPeriod = 'today' | 'upcoming' | 'history';
export type PickupChannel = 'phone' | 'email' | 'website' | 'platform' | 'other';

export interface PickupOrder {
  id: string;
  tenant_id: string;
  source_company_id: string | null;
  source_person_id: string | null;
  company_name: string | null;
  contact_name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  instructions: string | null;
  status: PickupStatus;
  channel: PickupChannel;
  scheduled_date: string | null;
  address_street: string | null;
  address_number: string | null;
  address_block: string | null;
  address_staircase: string | null;
  address_apartment: string | null;
  address_locality: string | null;
  maps_query: string | null;
  zone_id: string | null;
  zone_name: string | null;
  zone_color: string | null;
  zone_priority: number | null;
  rescheduled_from: string | null;
  origin: string;
  driver_id: string | null;
  driver_name: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  route_order: number | null;
  time_window_start: string | null;
  time_window_end: string | null;
  messaging_opt_out: boolean;
  completed_at: string | null;
  field_note: string | null;
  has_signature: boolean;
  // false → badge shown: 'done' order with company link but no finalized
  // collection found for that company on scheduled_date.
  has_collection: boolean;
  // Non-blocking: scheduled_date falls on a day the source company has
  // marked unavailable (companies.availability).
  availability_warning: boolean;
  // Non-blocking: scheduled_date falls on a day the tenant firm doesn't work
  // (tenant_profile.availability). Independent of availability_warning above.
  own_availability_warning: boolean;
  created_at: string;
  updated_at: string;
}

export interface PickupListMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export type StatusGroup = 'active' | 'completed' | 'cancelled';

export interface PickupListParams {
  page?: number;
  page_size?: number;
  status?: PickupStatus;
  status_group?: StatusGroup;
  unplanned?: boolean;
  period?: PickupPeriod;
  scheduled_date?: string;
  zone_id?: string;
  driver_id?: string;
  search?: string;
}

export interface PickupOrderInput {
  source_company_id?: string | null;
  source_person_id?: string | null;
  contact_name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  instructions?: string | null;
  channel?: PickupChannel;
  scheduled_date?: string | null;
  zone_id?: string | null;
  driver_id?: string | null;
  vehicle_id?: string | null;
  time_window_start?: string | null;
  time_window_end?: string | null;
  messaging_opt_out?: boolean;
  address_street?: string | null;
  address_number?: string | null;
  address_block?: string | null;
  address_staircase?: string | null;
  address_apartment?: string | null;
  address_locality?: string | null;
}

export interface PickupZone {
  id: string;
  name: string;
  color: string;
  priority: number;
}

export interface BoardDriverColumn {
  driver_id: string;
  driver_name: string;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  last_location: { lat: number; lng: number; captured_at: string } | null;
  orders: PickupOrder[];
}

export interface BoardData {
  unassigned: PickupOrder[];
  by_driver: BoardDriverColumn[];
}

const KEY = ['pickup-orders'] as const;

function buildQuery(params: PickupListParams): string {
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', String(params.page));
  if (params.page_size) sp.set('page_size', String(params.page_size));
  if (params.status) sp.set('status', params.status);
  if (params.status_group) sp.set('status_group', params.status_group);
  if (params.unplanned) sp.set('unplanned', 'true');
  if (params.period) sp.set('period', params.period);
  if (params.scheduled_date) sp.set('scheduled_date', params.scheduled_date);
  if (params.zone_id) sp.set('zone_id', params.zone_id);
  if (params.driver_id) sp.set('driver_id', params.driver_id);
  if (params.search) sp.set('search', params.search);
  const q = sp.toString();
  return q ? `?${q}` : '';
}

export function usePickupOrders(params: PickupListParams) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: () =>
      http.get<{ success: true; data: PickupOrder[]; meta: PickupListMeta }>(
        `/api/pickup-orders${buildQuery(params)}`,
      ),
    staleTime: 15_000,
  });
}

export function usePickupZones() {
  return useQuery({
    queryKey: [...KEY, 'zones'],
    queryFn: () =>
      http.get<{ success: true; data: PickupZone[] }>('/api/pickup-orders/zones').then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useCreatePickupZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color: string }) =>
      http.post<{ success: true; data: PickupZone }>('/api/pickup-orders/zones', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePickupZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; color?: string; priority?: number }) =>
      http.patch<{ success: true; data: PickupZone }>(`/api/pickup-orders/zones/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePickupZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.delete(`/api/pickup-orders/zones/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReschedulePickupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduled_date }: { id: string; scheduled_date: string }) =>
      http
        .post<{ success: true; data: PickupOrder }>(`/api/pickup-orders/${id}/reschedule`, { scheduled_date })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Portal requests the operator hasn't opened yet — badge on the menu item. */
export function useUnseenPickupCount(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'unseen-count'],
    queryFn: () =>
      http
        .get<{ success: true; data: { count: number } }>('/api/pickup-orders/unseen-count')
        .then((r) => r.data.count),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useMarkPickupsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => http.post('/api/pickup-orders/mark-seen', {}),
    onSuccess: () => qc.setQueryData([...KEY, 'unseen-count'], 0),
  });
}

export function useCreatePickupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PickupOrderInput) =>
      http.post<{ success: true; data: PickupOrder }>('/api/pickup-orders', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePickupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<PickupOrderInput>) =>
      http.patch<{ success: true; data: PickupOrder }>(`/api/pickup-orders/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelPickupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      http.post<{ success: true; data: PickupOrder }>(`/api/pickup-orders/${id}/cancel`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeletePickupOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => http.delete(`/api/pickup-orders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBoardData(date: string) {
  return useQuery({
    queryKey: [...KEY, 'board', date],
    queryFn: () =>
      http.get<{ success: true; data: BoardData }>(`/api/pickup-orders/board?date=${date}`).then((r) => r.data),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useReorderPickupOrders() {
  const qc = useQueryClient();
  return useMutation({
    // `driver_id` set = the orders were dropped into that driver's board
    // column (null = unassigned) and move there with their new positions.
    mutationFn: ({
      orders,
      driver_id,
    }: {
      orders: Array<{ id: string; route_order: number }>;
      driver_id?: string | null;
    }) =>
      http
        .patch<{ success: true; data: { updated: number } }>('/api/pickup-orders/reorder', {
          orders,
          ...(driver_id !== undefined ? { driver_id } : {}),
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
